/**
 * CSV import/export (ARCHITECTURE §3.2, coexistence per PRD). Exports are
 * read-only snapshots a user can hand to an auditor or bank; import parses an
 * external file into a preview so columns can be mapped to journal lines.
 */
import type { BankTxn, Currency, JournalEntry, Money, TrialBalance } from "@core/index";
import { format, parseAmount, subtract, zero } from "@core/index";

// Cells whose first character is one of these are interpreted as a formula by
// Excel / Google Sheets / LibreOffice on open. Since exports are explicitly
// meant to be handed to an auditor or bank, an attacker-controlled free-text
// field (description, account name, entity id) — or even a negative amount
// rendered as "-1,234.56" — would otherwise become live formula/DDE execution.
const FORMULA_LEAD = /^[=+\-@\t\r]/;
// Genuine numbers (incl. "-1,234.56") are left intact so exports stay usable.
const NUMERIC = /^-?[\d,]*\.?\d+$/;

/** Neutralize CSV formula injection: prefix risky non-numeric cells with a quote. */
function neutralizeFormula(value: string): string {
  return FORMULA_LEAD.test(value) && !NUMERIC.test(value) ? `'${value}` : value;
}

function csvEscape(value: string): string {
  const safe = neutralizeFormula(value);
  if (/[",\n\r]/.test(safe)) return `"${safe.replace(/"/g, '""')}"`;
  return safe;
}

export function rowsToCsv(headers: readonly string[], rows: readonly (readonly string[])[]): string {
  const lines = [headers, ...rows].map((r) => r.map(csvEscape).join(","));
  return lines.join("\r\n");
}

/** Trial balance → CSV (code, account, debit, credit) with a totals row. */
export function trialBalanceToCsv(tb: TrialBalance, locale = "en-AE"): string {
  const rows = tb.rows.map((r) => [
    r.code,
    r.name,
    format(r.debit, locale, { parenNegative: false }),
    format(r.credit, locale, { parenNegative: false }),
  ]);
  rows.push([
    "",
    "TOTAL",
    format(tb.debitTotal, locale, { parenNegative: false }),
    format(tb.creditTotal, locale, { parenNegative: false }),
  ]);
  return rowsToCsv(["Code", "Account", "Debit (AED)", "Credit (AED)"], rows);
}

/** Journal → CSV, one row per line (ref, date, description, account, dr, cr). */
export function entriesToCsv(entries: readonly JournalEntry[], locale = "en-AE"): string {
  const rows: string[][] = [];
  for (const e of entries) {
    for (const l of e.lines) {
      rows.push([
        e.ref,
        e.date,
        e.description,
        e.entityId,
        l.accountCode,
        format(l.debit, locale, { parenNegative: false }),
        format(l.credit, locale, { parenNegative: false }),
        e.status,
      ]);
    }
  }
  return rowsToCsv(
    ["Ref", "Date", "Description", "Entity", "Account", "Debit", "Credit", "Status"],
    rows,
  );
}

/** Minimal RFC-4180-ish parser handling quoted fields and CRLF/LF. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      row.push(field);
      field = "";
    } else if (ch === "\n") {
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else if (ch !== "\r") {
      field += ch;
    }
  }
  if (field !== "" || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => c.trim() !== ""));
}

export interface ImportPreview {
  readonly headers: readonly string[];
  readonly rows: readonly (readonly string[])[];
  readonly rowCount: number;
}

/** Parse an uploaded CSV into a header + rows preview for the mapping step. */
export function previewImport(text: string): ImportPreview {
  const parsed = parseCsv(text);
  if (parsed.length === 0) return { headers: [], rows: [], rowCount: 0 };
  const [headers = [], ...rows] = parsed;
  return { headers, rows, rowCount: rows.length };
}

// ---------- Bank statement import (reconciliation) ----------

/** Normalize a bank date cell to ISO (YYYY-MM-DD). UAE convention is DD/MM/YYYY. */
function toIsoDate(raw: string): string {
  const s = raw.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = /^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/.exec(s);
  if (m) {
    const [, d = "", mo = "", y = ""] = m;
    const day = d.padStart(2, "0");
    const month = mo.padStart(2, "0");
    const year = y.length === 2 ? `20${y}` : y;
    return `${year}-${month}-${day}`;
  }
  return s;
}

function safeAmount(cell: string | undefined, base: Currency): Money {
  if (!cell || cell.trim() === "") return zero(base);
  try {
    return parseAmount(cell, base);
  } catch {
    return zero(base);
  }
}

/** Index of the first header matching any of the given patterns, or -1. */
function findCol(headers: readonly string[], patterns: readonly RegExp[]): number {
  return headers.findIndex((h) => patterns.some((p) => p.test(h.toLowerCase().trim())));
}

export interface ParsedBankStatement {
  readonly txns: readonly BankTxn[];
  readonly headers: readonly string[];
  /** Data rows read from the file (excluding the header row). */
  readonly rowCount: number;
  /** True when a usable date + amount mapping was detected. */
  readonly recognized: boolean;
}

/**
 * Parse an uploaded bank statement CSV into normalized transactions. Column
 * layout is detected heuristically: a date column, a description column, and
 * either a single signed amount column or separate debit/credit columns
 * (statement convention: a credit is money in, a debit is money out). Amounts
 * become integer minor units — never rounded floats. Positive = money in.
 */
export function parseBankStatement(text: string, base: Currency): ParsedBankStatement {
  const parsed = parseCsv(text);
  if (parsed.length === 0) {
    return { txns: [], headers: [], rowCount: 0, recognized: false };
  }
  const [headers = [], ...rows] = parsed;

  const dateCol = Math.max(0, findCol(headers, [/date/, /^value$/, /posted/]));
  let descCol = findCol(headers, [/desc/, /narrative/, /detail/, /particular/, /reference/, /memo/, /payee/]);
  if (descCol < 0) descCol = dateCol === 0 ? 1 : 0;
  const amountCol = findCol(headers, [/^amount$/, /^value$/, /^amount \(/]);
  const debitCol = findCol(headers, [/debit/, /withdrawal/, /paid out/, /^dr$/]);
  const creditCol = findCol(headers, [/credit/, /deposit/, /paid in/, /^cr$/]);

  const hasAmount = amountCol >= 0;
  const hasDrCr = debitCol >= 0 || creditCol >= 0;
  const recognized = hasAmount || hasDrCr;

  const txns: BankTxn[] = [];
  rows.forEach((row, i) => {
    const date = toIsoDate(row[dateCol] ?? "");
    const description = (row[descCol] ?? "").trim();
    let amount: Money;
    if (hasAmount) {
      amount = safeAmount(row[amountCol], base);
    } else {
      const credit = safeAmount(creditCol >= 0 ? row[creditCol] : "", base);
      const debit = safeAmount(debitCol >= 0 ? row[debitCol] : "", base);
      amount = subtract(credit, debit); // credit in, debit out
    }
    if (amount.amount === 0) return; // skip opening-balance / blank rows
    txns.push({ id: `bank-${i}`, date, description, amount });
  });

  return { txns, headers, rowCount: rows.length, recognized };
}

/** Trigger a browser download of a CSV string (no-op outside the browser). */
export function downloadCsv(filename: string, csv: string): void {
  if (typeof document === "undefined" || typeof URL.createObjectURL !== "function") return;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
